// D:\my-website\src\components\AdminDashboard.js

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { db } from '../firebase'; // 确保路径正确，导入 Firebase db 实例
import { doc, setDoc } from 'firebase/firestore'; // 导入 Firestore 的 doc 和 setDoc 函数

// 导入默认内容数据 (确保路径正确，这应该与 App.js 导入的 defaultContent 来源一致)
// 如果您没有 defaultContent.js 文件，请根据您的项目实际情况调整或创建
import {
    defaultHomepageSettings,
    defaultAboutUsContent,
    defaultBusinessOutlookContent,
    defaultIndustryDynamicsContent, // 确保这里导入了新的默认结构
    defaultContactInfo
} from '../utils/defaultContent'; // 假设 defaultContent 在 src/utils 目录下

function AdminDashboard({
    homepageSettings: propHomepageSettings, // 从 App.js 接收的 props
    setHomepageSettings: setAppHomepageSettings, // 用于更新 App.js 中的状态
    aboutUsContent: propAboutUsContent,
    setAboutUsContent: setAppAboutUsContent,
    businessOutlookContent: propBusinessOutlookContent,
    setBusinessOutlookContent: setAppBusinessOutlookContent,
    industryDynamicsContent: propIndustryDynamicsContent,
    setIndustryDynamicsContent: setAppIndustryDynamicsContent,
    contactInfo: propContactInfo,
    setContactInfo: setAppContactInfo,
}) {
    const { t } = useTranslation();
    const [currentAdminTab, setCurrentAdminTab] = useState('homepage'); // 当前选中的管理标签页

    // 各个内容的本地状态，用于表单编辑
    const [currentHomepageSettings, setCurrentHomepageSettings] = useState(propHomepageSettings || defaultHomepageSettings);
    const [currentAboutUsContent, setCurrentAboutUsContent] = useState(propAboutUsContent || defaultAboutUsContent);
    const [currentBusinessOutlookContent, setCurrentBusinessOutlookContent] = useState(propBusinessOutlookContent || defaultBusinessOutlookContent);
    // 更新行业动态的本地状态，以匹配新的 newsItems 结构
    const [currentIndustryDynamicsContent, setCurrentIndustryDynamicsContent] = useState(propIndustryDynamicsContent || defaultIndustryDynamicsContent);
    const [currentContactInfo, setCurrentContactInfo] = useState(propContactInfo || defaultContactInfo);

    const [updateStatus, setUpdateStatus] = useState(''); // 用于显示更新状态信息

    // 当从 App.js 接收的 props 变化时，更新本地状态
    useEffect(() => {
        if (propHomepageSettings) setCurrentHomepageSettings(propHomepageSettings);
    }, [propHomepageSettings]);
    useEffect(() => {
        if (propAboutUsContent) setCurrentAboutUsContent(propAboutUsContent);
    }, [propAboutUsContent]);
    useEffect(() => {
        if (propBusinessOutlookContent) setCurrentBusinessOutlookContent(propBusinessOutlookContent);
    }, [propBusinessOutlookContent]);
    // 更新行业动态的 useEffect，以匹配新的 newsItems 结构
    useEffect(() => {
        // 确保 propIndustryDynamicsContent.newsItems 是一个数组
        if (propIndustryDynamicsContent && Array.isArray(propIndustryDynamicsContent.newsItems)) {
            setCurrentIndustryDynamicsContent(propIndustryDynamicsContent);
        } else {
            // 如果 propIndustryDynamicsContent 不存在或 newsItems 不是数组，使用默认值
            setCurrentIndustryDynamicsContent(defaultIndustryDynamicsContent);
        }
    }, [propIndustryDynamicsContent]);
    useEffect(() => {
        if (propContactInfo) setCurrentContactInfo(propContactInfo);
    }, [propContactInfo]);


    // --- 通用处理函数 ---

    // 获取当前应用的 ID (从全局变量或默认值)
    const appId = typeof window.__app_id !== 'undefined' ? window.__app_id : 'default-app-id-for-local'; // 与 App.js 中的逻辑保持一致

    // 处理主页设置的输入变化
    const handleHomepageSettingChange = (e) => {
        const { name, value } = e.target;
        setCurrentHomepageSettings(prev => ({ ...prev, [name]: value }));
    };

    // 保存主页设置到 Firestore
    const handleUpdateHomepageSettings = async () => {
        setUpdateStatus(t('admin_update_status_updating'));
        try {
            // Firestore 文档路径：artifacts/{appId}/public/data/homepage_settings/main_settings
            const homepageSettingsDocRef = doc(db, `artifacts/${appId}/public/data/homepage_settings`, 'main_settings');
            await setDoc(homepageSettingsDocRef, currentHomepageSettings, { merge: true });
            setUpdateStatus(t('admin_update_status_success_indicator')); // 使用国际化成功的关键词
            setAppHomepageSettings(currentHomepageSettings); // 更新 App.js 中的状态
        } catch (error) {
            console.error("更新主页设置时出错:", error);
            setUpdateStatus(`${t('admin_update_status_failed')}: ${error.message}`);
        }
    };

    // 处理关于我们内容的输入变化
    const handleAboutUsContentChange = (e) => {
        const { name, value } = e.target;
        setCurrentAboutUsContent(prev => ({ ...prev, [name]: value }));
    };

    // 处理团队成员信息的输入变化
    const handleTeamMemberChange = (index, field, value) => {
        const updatedMembers = [...(currentAboutUsContent.teamMembers || [])];
        updatedMembers[index] = { ...updatedMembers[index], [field]: value };
        setCurrentAboutUsContent(prev => ({ ...prev, teamMembers: updatedMembers }));
    };

    // 添加新团队成员
    const handleAddTeamMember = () => {
        setCurrentAboutUsContent(prev => ({
            ...prev,
            teamMembers: [...(prev.teamMembers || []), { name: '', title: '', bio: '', imageUrl: 'https://placehold.co/150x150/a7d9ff/007bff?text=New+Member' }]
        }));
    };

    // 移除团队成员
    const handleRemoveTeamMember = (index) => {
        const updatedMembers = (currentAboutUsContent.teamMembers || []).filter((_, i) => i !== index);
        setCurrentAboutUsContent(prev => ({ ...prev, teamMembers: updatedMembers }));
    };

    // 处理核心优势的输入变化
    const handleCoreAdvantageChange = (index, field, value) => {
        const updatedAdvantages = [...(currentAboutUsContent.coreAdvantages || [])];
        updatedAdvantages[index] = { ...updatedAdvantages[index], [field]: value };
        setCurrentAboutUsContent(prev => ({ ...prev, coreAdvantages: updatedAdvantages }));
    };

    // 添加新核心优势
    const handleAddCoreAdvantage = () => {
        setCurrentAboutUsContent(prev => ({
            ...prev,
            coreAdvantages: [...(prev.coreAdvantages || []), { title: '', points: [''] }]
        }));
    };

    // 移除核心优势
    const handleRemoveCoreAdvantage = (index) => {
        const updatedAdvantages = (currentAboutUsContent.coreAdvantages || []).filter((_, i) => i !== index);
        setCurrentAboutUsContent(prev => ({ ...prev, coreAdvantages: updatedAdvantages }));
    };

    // 处理核心优势要点的输入变化
    const handleCoreAdvantagePointChange = (advIndex, pointIndex, value) => {
        const updatedAdvantages = [...(currentAboutUsContent.coreAdvantages || [])];
        if (updatedAdvantages[advIndex] && updatedAdvantages[advIndex].points) {
            updatedAdvantages[advIndex].points[pointIndex] = value;
        }
        setCurrentAboutUsContent(prev => ({ ...prev, coreAdvantages: updatedAdvantages }));
    };

    // 添加核心优势要点
    const handleAddCoreAdvantagePoint = (advIndex) => {
        const updatedAdvantages = [...(currentAboutUsContent.coreAdvantages || [])];
        if (updatedAdvantages[advIndex]) {
            updatedAdvantages[advIndex].points = [...(updatedAdvantages[advIndex].points || []), ''];
        }
        setCurrentAboutUsContent(prev => ({ ...prev, coreAdvantages: updatedAdvantages }));
    };

    // 移除核心优势要点
    const handleRemoveCoreAdvantagePoint = (advIndex, pointIndex) => {
        const updatedAdvantages = [...(currentAboutUsContent.coreAdvantages || [])];
        if (updatedAdvantages[advIndex] && updatedAdvantages[advIndex].points) {
            updatedAdvantages[advIndex].points.splice(pointIndex, 1);
        }
        setCurrentAboutUsContent(prev => ({
            ...prev,
            coreAdvantages: updatedAdvantages
        }));
    };

    // 保存关于我们内容到 Firestore
    const handleUpdateAboutUsContent = async () => {
        setUpdateStatus(t('admin_update_status_updating'));
        try {
            const aboutUsDocRef = doc(db, `artifacts/${appId}/public/data/about_us_content`, 'main_content');
            await setDoc(aboutUsDocRef, currentAboutUsContent, { merge: true });
            setUpdateStatus(t('admin_update_status_success_indicator'));
            setAppAboutUsContent(currentAboutUsContent);
        } catch (error) {
            console.error("更新关于我们内容时出错:", error);
            setUpdateStatus(`${t('admin_update_status_failed')}: ${error.message}`);
        }
    };

    // 处理业务与展望内容的输入变化
    const handleBusinessOutlookContentChange = (e) => {
        const { name, value } = e.target;
        setCurrentBusinessOutlookContent(prev => ({ ...prev, [name]: value }));
    };

    // 处理业务范围项的输入变化
    const handleBusinessScopeItemChange = (index, field, value) => {
        const updatedItems = [...(currentBusinessOutlookContent.businessScopeItems || [])];
        updatedItems[index] = { ...updatedItems[index], [field]: value };
        setCurrentBusinessOutlookContent(prev => ({ ...prev, businessScopeItems: updatedItems }));
    };

    // 处理业务范围要点的输入变化
    const handleBusinessScopePointChange = (itemIndex, pointIndex, value) => {
        const updatedItems = [...(currentBusinessOutlookContent.businessScopeItems || [])];
        if (updatedItems[itemIndex] && updatedItems[itemIndex].points) {
            updatedItems[itemIndex].points[pointIndex] = value;
        }
        setCurrentBusinessOutlookContent(prev => ({ ...prev, businessScopeItems: updatedItems }));
    };

    // 处理业务拓展项的输入变化
    const handleBusinessExpansionItemChange = (index, field, value) => {
        const updatedItems = [...(currentBusinessOutlookContent.businessExpansionItems || [])];
        updatedItems[index] = { ...updatedItems[index], [field]: value };
        setCurrentBusinessOutlookContent(prev => ({ ...prev, businessExpansionItems: updatedItems }));
    };

    // 处理业务拓展要点的输入变化
    const handleBusinessExpansionPointChange = (itemIndex, pointIndex, value) => {
        const updatedItems = [...(currentBusinessOutlookContent.businessExpansionItems || [])];
        if (updatedItems[itemIndex] && updatedItems[itemIndex].points) {
            updatedItems[itemIndex].points[pointIndex] = value;
        }
        setCurrentBusinessOutlookContent(prev => ({ ...prev, businessExpansionItems: updatedItems }));
    };

    // 处理财务营收预测表格的输入变化
    const handleFinancialRevenueChange = (index, field, value) => {
        const updatedTable = [...((currentBusinessOutlookContent.financialForecast && currentBusinessOutlookContent.financialForecast.revenueTable) || [])];
        updatedTable[index] = { ...updatedTable[index], [field]: parseFloat(value) || 0 };
        setCurrentBusinessOutlookContent(prev => ({
            ...prev,
            financialForecast: { ...(prev.financialForecast || {}), revenueTable: updatedTable }
        }));
    };

    // 处理财务成本假设的输入变化
    const handleFinancialCostAssumptionChange = (index, field, value) => {
        const updatedAssumptions = [...((currentBusinessOutlookContent.financialForecast && currentBusinessOutlookContent.financialForecast.costAssumptions) || [])];
        updatedAssumptions[index] = { ...updatedAssumptions[index], [field]: value };
        setCurrentBusinessOutlookContent(prev => ({
            ...prev,
            financialForecast: { ...(prev.financialForecast || {}), costAssumptions: updatedAssumptions }
        }));
    };

    // 处理财务成本详情的输入变化
    const handleFinancialCostDetailChange = (assumpIndex, detailIndex, value) => {
        const updatedAssumptions = [...((currentBusinessOutlookContent.financialForecast && currentBusinessOutlookContent.financialForecast.costAssumptions) || [])];
        if (updatedAssumptions[assumpIndex] && updatedAssumptions[assumpIndex].details) {
            updatedAssumptions[assumpIndex].details[detailIndex] = value;
        }
        setCurrentBusinessOutlookContent(prev => ({
            ...prev,
            financialForecast: { ...(prev.financialForecast || {}), costAssumptions: updatedAssumptions }
        }));
    };

    // 处理财务成本详情的添加
    const handleAddFinancialCostDetail = (assumpIndex) => {
        const updatedAssumptions = [...((currentBusinessOutlookContent.financialForecast && currentBusinessOutlookContent.financialForecast.costAssumptions) || [])];
        if (updatedAssumptions[assumpIndex]) {
            updatedAssumptions[assumpIndex].details = [...(updatedAssumptions[assumpIndex].details || []), ''];
        }
        setCurrentBusinessOutlookContent(prev => ({
            ...prev,
            financialForecast: { ...(prev.financialForecast || {}), costAssumptions: updatedAssumptions }
        }));
    };

    // 处理财务成本详情的移除
    const handleRemoveFinancialCostDetail = (assumpIndex, detailIndex) => {
        const updatedAssumptions = [...((currentBusinessOutlookContent.financialForecast && currentBusinessOutlookContent.financialForecast.costAssumptions) || [])];
        if (updatedAssumptions[assumpIndex] && updatedAssumptions[assumpIndex].details) {
            updatedAssumptions[assumpIndex].details.splice(detailIndex, 1);
        }
        setCurrentBusinessOutlookContent(prev => ({
            ...prev,
            financialForecast: { ...(prev.financialForecast || {}), costAssumptions: updatedAssumptions }
        }));
    };

    // 处理投资机会的输入变化
    const handleFinancialInvestmentOpportunityChange = (e) => {
        const { value } = e.target;
        setCurrentBusinessOutlookContent(prev => ({
            ...prev,
            financialForecast: { ...(prev.financialForecast || {}), investmentOpportunity: value }
        }));
    };

    // 保存业务与展望内容到 Firestore
    const handleUpdateBusinessOutlookContent = async () => {
        setUpdateStatus(t('admin_update_status_updating'));
        try {
            const businessOutlookDocRef = doc(db, `artifacts/${appId}/public/data/business_outlook_content`, 'main_content');
            await setDoc(businessOutlookDocRef, currentBusinessOutlookContent, { merge: true });
            setUpdateStatus(t('admin_update_status_success_indicator'));
            setAppBusinessOutlookContent(currentBusinessOutlookContent);
        } catch (error) {
            console.error("更新业务与展望内容时出错:", error);
            setUpdateStatus(`${t('admin_update_status_failed')}: ${error.message}`);
        }
    };

    // --- 行业动态新闻管理 ---

    // 处理新闻项的输入变化
    const handleNewsItemChange = (index, field, value) => {
        const updatedNewsItems = [...(currentIndustryDynamicsContent.newsItems || [])];
        updatedNewsItems[index] = { ...updatedNewsItems[index], [field]: value };
        setCurrentIndustryDynamicsContent(prev => ({ ...prev, newsItems: updatedNewsItems }));
    };

    // 添加新新闻项
    const handleAddNewsItem = () => {
        setCurrentIndustryDynamicsContent(prev => ({
            ...prev,
            newsItems: [...(prev.newsItems || []), { id: Date.now().toString(), title: '', date: '', media: '', content: '' }]
        }));
    };

    // 移除新闻项
    const handleRemoveNewsItem = (index) => {
        const updatedNewsItems = (currentIndustryDynamicsContent.newsItems || []).filter((_, i) => i !== index);
        setCurrentIndustryDynamicsContent(prev => ({ ...prev, newsItems: updatedNewsItems }));
    };

    // 保存行业动态内容到 Firestore
    const handleUpdateIndustryDynamicsContent = async () => {
        setUpdateStatus(t('admin_update_status_updating'));
        try {
            const industryDynamicsDocRef = doc(db, `artifacts/${appId}/public/data/industry_dynamics_content`, 'main_content');
            await setDoc(industryDynamicsDocRef, currentIndustryDynamicsContent, { merge: true });
            setUpdateStatus(t('admin_update_status_success_indicator'));
            setAppIndustryDynamicsContent(currentIndustryDynamicsContent);
        } catch (error) {
            console.error("更新行业动态内容时出错:", error);
            setUpdateStatus(`${t('admin_update_status_failed')}: ${error.message}`);
        }
    };

    // 处理联系信息的输入变化
    const handleContactInfoChange = (e) => {
        const { name, value } = e.target;
        setCurrentContactInfo(prev => ({ ...prev, [name]: value }));
    };

    // 保存联系信息到 Firestore
    const handleUpdateContactInfo = async () => {
        setUpdateStatus(t('admin_update_status_updating'));
        try {
            const contactInfoDocRef = doc(db, `artifacts/${appId}/public/data/contact_info`, 'main_contact');
            await setDoc(contactInfoDocRef, currentContactInfo, { merge: true });
            setUpdateStatus(t('admin_update_status_success_indicator'));
            setAppContactInfo(currentContactInfo);
        } catch (error) {
            console.error("更新联系信息时出错:", error);
            setUpdateStatus(`${t('admin_update_status_failed')}: ${error.message}`);
        }
    };

    return (
        <section id="admin" className="py-16 md:py-24 bg-gray-100 rounded-lg shadow-md m-4">
            <div className="container mx-auto px-4 md:px-6">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-10 section-title">{t('admin_dashboard_title')}</h2>
                <div className="bg-white p-8 rounded-lg shadow-lg">
                    <div className="flex border-b border-gray-200 mb-6 overflow-x-auto"> {/* Added overflow-x-auto for smaller screens */}
                        <button
                            className={`py-2 px-4 text-lg font-medium whitespace-nowrap ${currentAdminTab === 'homepage' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-blue-600'}`}
                            onClick={() => setCurrentAdminTab('homepage')}
                        >
                            {t('admin_tab_homepage_settings')}
                        </button>
                        <button
                            className={`py-2 px-4 text-lg font-medium whitespace-nowrap ${currentAdminTab === 'aboutUs' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-blue-600'}`}
                            onClick={() => setCurrentAdminTab('aboutUs')}
                        >
                            {t('admin_tab_about_us')}
                        </button>
                        <button
                            className={`py-2 px-4 text-lg font-medium whitespace-nowrap ${currentAdminTab === 'businessOutlook' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-blue-600'}`}
                            onClick={() => setCurrentAdminTab('businessOutlook')}
                        >
                            {t('admin_tab_business_outlook')}
                        </button>
                        <button
                            className={`py-2 px-4 text-lg font-medium whitespace-nowrap ${currentAdminTab === 'industryDynamics' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-blue-600'}`}
                            onClick={() => setCurrentAdminTab('industryDynamics')}
                        >
                            {t('admin_tab_industry_dynamics')}
                        </button>
                        <button
                            className={`py-2 px-4 text-lg font-medium whitespace-nowrap ${currentAdminTab === 'contact' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-blue-600'}`}
                            onClick={() => setCurrentAdminTab('contact')}
                        >
                            {t('admin_tab_contact_info')}
                        </button>
                    </div>

                    {updateStatus && (
                        <div className={`p-3 mb-4 rounded-md ${updateStatus.includes(t('admin_update_status_success_indicator')) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {updateStatus}
                        </div>
                    )}

                    {/* 主页设置标签页 */}
                    {currentAdminTab === 'homepage' && (
                        <div className="space-y-6">
                            <h3 className="text-2xl font-semibold text-gray-800 mb-4">{t('admin_homepage_content_edit_title')}</h3>
                            <div>
                                <label htmlFor="heroTitle" className="block text-gray-700 text-sm font-bold mb-2">{t('admin_homepage_hero_title_label')}:</label>
                                <input
                                    type="text"
                                    id="heroTitle"
                                    name="heroTitle"
                                    value={currentHomepageSettings.heroTitle || ''}
                                    onChange={handleHomepageSettingChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                />
                            </div>
                            <div>
                                <label htmlFor="heroDescription" className="block text-gray-700 text-sm font-bold mb-2">{t('admin_homepage_description_label')}:</label>
                                <textarea
                                    id="heroDescription"
                                    name="heroDescription"
                                    value={currentHomepageSettings.heroDescription || ''}
                                    onChange={handleHomepageSettingChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-24"
                                />
                            </div>
                            <div>
                                <label htmlFor="heroButtonText" className="block text-gray-700 text-sm font-bold mb-2">{t('admin_homepage_button_text_label')}:</label>
                                <input
                                    type="text"
                                    id="heroButtonText"
                                    name="heroButtonText"
                                    value={currentHomepageSettings.heroButtonText || ''}
                                    onChange={handleHomepageSettingChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                />
                            </div>
                            <div>
                                <label htmlFor="heroBackgroundImageUrl" className="block text-gray-700 text-sm font-bold mb-2">{t('admin_homepage_bg_image_url_label')}:</label>
                                <input
                                    type="text"
                                    id="heroBackgroundImageUrl"
                                    name="heroBackgroundImageUrl"
                                    value={currentHomepageSettings.heroBackgroundImageUrl || ''}
                                    onChange={handleHomepageSettingChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                />
                                {currentHomepageSettings.heroBackgroundImageUrl && (
                                    <img src={currentHomepageSettings.heroBackgroundImageUrl} alt={t('admin_homepage_bg_preview_alt')} className="mt-2 w-32 h-auto rounded-md" onError={(e) => e.target.src = 'https://placehold.co/100x60/cccccc/333333?text=Image+Error'}/>
                                )}
                            </div>
                            <div>
                                <label htmlFor="logoUrl" className="block text-gray-700 text-sm font-bold mb-2">{t('admin_homepage_logo_url_label')}:</label>
                                <input
                                    type="text"
                                    id="logoUrl"
                                    name="logoUrl"
                                    value={currentHomepageSettings.logoUrl || ''}
                                    onChange={handleHomepageSettingChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                />
                                {currentHomepageSettings.logoUrl && (
                                    <img src={currentHomepageSettings.logoUrl} alt={t('admin_homepage_logo_preview_alt')} className="mt-2 w-16 h-16 rounded-full" onError={(e) => e.target.src = 'https://placehold.co/40x40/cccccc/333333?text=Logo+Error'}/>
                                )}
                            </div>
                            <button
                                onClick={handleUpdateHomepageSettings}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out transform hover:scale-105"
                            >
                                {t('admin_save_homepage_settings_button')}
                            </button>
                        </div>
                    )}

                    {/* 关于我们标签页 */}
                    {currentAdminTab === 'aboutUs' && (
                        <div className="space-y-6">
                            <h3 className="text-2xl font-semibold text-gray-800 mb-4">{t('admin_about_us_content_edit_title')}</h3>
                            <div>
                                <label htmlFor="aboutText1" className="block text-gray-700 text-sm font-bold mb-2">{t('admin_about_us_text1_label')}:</label>
                                <textarea
                                    id="aboutText1"
                                    name="aboutText1"
                                    value={currentAboutUsContent.aboutText1 || ''}
                                    onChange={handleAboutUsContentChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-24"
                                />
                            </div>
                            <div>
                                <label htmlFor="aboutText2" className="block text-gray-700 text-sm font-bold mb-2">{t('admin_about_us_text2_label')}:</label>
                                <textarea
                                    id="aboutText2"
                                    name="aboutText2"
                                    value={currentAboutUsContent.aboutText2 || ''}
                                    onChange={handleAboutUsContentChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-24"
                                />
                            </div>
                            <div>
                                <label htmlFor="aboutImageUrl" className="block text-gray-700 text-sm font-bold mb-2">{t('admin_about_us_image_url_label')}:</label>
                                <input
                                    type="text"
                                    id="aboutImageUrl"
                                    name="aboutImageUrl"
                                    value={currentAboutUsContent.aboutImageUrl || ''}
                                    onChange={handleAboutUsContentChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                />
                                {currentAboutUsContent.aboutImageUrl && (
                                    <img src={currentAboutUsContent.aboutImageUrl} alt={t('admin_about_us_preview_alt')} className="mt-2 w-32 h-auto rounded-md" onError={(e) => e.target.src = 'https://placehold.co/100x60/cccccc/333333?text=Image+Error'}/>
                                )}
                            </div>
                            <div>
                                <label htmlFor="teamIntro" className="block text-gray-700 text-sm font-bold mb-2">{t('admin_about_us_team_intro_label')}:</label>
                                <textarea
                                    id="teamIntro"
                                    name="teamIntro"
                                    value={currentAboutUsContent.teamIntro || ''}
                                    onChange={handleAboutUsContentChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-24"
                                />
                            </div>

                            <h4 className="text-xl font-semibold text-gray-800 mt-6 mb-4">{t('admin_about_us_team_members_title')}</h4>
                            {(currentAboutUsContent.teamMembers || []).map((member, index) => (
                                <div key={index} className="border p-4 rounded-md mb-4 bg-gray-50">
                                    <h5 className="font-medium mb-2">{t('admin_about_us_member')} {index + 1}</h5>
                                    <div className="space-y-2">
                                        <input
                                            type="text"
                                            placeholder={t('admin_about_us_member_name_placeholder')}
                                            value={member.name || ''}
                                            onChange={(e) => handleTeamMemberChange(index, 'name', e.target.value)}
                                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        />
                                        <input
                                            type="text"
                                            placeholder={t('admin_about_us_member_title_placeholder')}
                                            value={member.title || ''}
                                            onChange={(e) => handleTeamMemberChange(index, 'title', e.target.value)}
                                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        />
                                        <textarea
                                            placeholder={t('admin_about_us_member_bio_placeholder')}
                                            value={member.bio || ''}
                                            onChange={(e) => handleTeamMemberChange(index, 'bio', e.target.value)}
                                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-20"
                                        />
                                        <input
                                            type="text"
                                            placeholder={t('admin_about_us_member_image_url_placeholder')}
                                            value={member.imageUrl || ''}
                                            onChange={(e) => handleTeamMemberChange(index, 'imageUrl', e.target.value)}
                                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        />
                                        {member.imageUrl && (
                                            <img src={member.imageUrl} alt={t('admin_about_us_member_preview_alt')} className="mt-2 w-20 h-20 rounded-full" onError={(e) => e.target.src = 'https://placehold.co/50x50/cccccc/333333?text=Image+Error'}/>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => handleRemoveTeamMember(index)}
                                        className="mt-4 bg-red-500 hover:bg-red-600 text-white py-1 px-3 rounded-md text-sm transition duration-300"
                                    >
                                        {t('admin_about_us_remove_member_button')}
                                    </button>
                                </div>
                            ))}
                            <button
                                onClick={handleAddTeamMember}
                                className="bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-md transition duration-300 ease-in-out transform hover:scale-105"
                            >
                                {t('admin_about_us_add_member_button')}
                            </button>

                            <h4 className="text-xl font-semibold text-gray-800 mt-6 mb-4">{t('admin_about_us_core_advantages_title')}</h4>
                            {(currentAboutUsContent.coreAdvantages || []).map((advantage, advIndex) => (
                                <div key={advIndex} className="border p-4 rounded-md mb-4 bg-gray-50">
                                    <h5 className="font-medium mb-2">{t('admin_about_us_advantage')} {advIndex + 1}</h5>
                                    <input
                                        type="text"
                                        placeholder={t('admin_about_us_advantage_title_placeholder')}
                                        value={advantage.title || ''}
                                        onChange={(e) => handleCoreAdvantageChange(advIndex, 'title', e.target.value)}
                                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-2"
                                    />
                                    <h6 className="font-medium mt-3 mb-1">{t('admin_about_us_points_label')}:</h6>
                                    {(advantage.points || []).map((point, pointIndex) => (
                                        <div key={pointIndex} className="flex items-center mb-2">
                                            <input
                                                type="text"
                                                placeholder={t('admin_about_us_point_description_placeholder')}
                                                value={point || ''}
                                                onChange={(e) => handleCoreAdvantagePointChange(advIndex, pointIndex, e.target.value)}
                                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mr-2"
                                            />
                                            <button
                                                onClick={() => handleRemoveCoreAdvantagePoint(advIndex, pointIndex)}
                                                className="bg-red-400 hover:bg-red-500 text-white py-1 px-2 rounded-md text-sm transition duration-300"
                                            >
                                                {t('admin_remove_button')}
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        onClick={() => handleAddCoreAdvantagePoint(advIndex)}
                                        className="bg-blue-400 hover:bg-blue-500 text-white py-1 px-3 rounded-md text-sm mt-2 transition duration-300"
                                    >
                                        {t('admin_add_point_button')}
                                    </button>
                                    <button
                                        onClick={() => handleRemoveCoreAdvantage(advIndex)}
                                        className="mt-4 bg-red-500 hover:bg-red-600 text-white py-1 px-3 rounded-md text-sm transition duration-300 ml-2"
                                    >
                                        {t('admin_about_us_remove_advantage_button')}
                                    </button>
                                </div>
                            ))}
                            <button
                                onClick={handleAddCoreAdvantage}
                                className="bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-md transition duration-300 ease-in-out transform hover:scale-105"
                            >
                                {t('admin_about_us_add_advantage_button')}
                            </button>

                            <button
                                onClick={handleUpdateAboutUsContent}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out transform hover:scale-105 mt-6"
                            >
                                {t('admin_save_about_us_button')}
                            </button>
                        </div>
                    )}

                    {/* 业务与展望标签页 */}
                    {currentAdminTab === 'businessOutlook' && (
                        <div className="space-y-6">
                            <h3 className="text-2xl font-semibold text-gray-800 mb-4">{t('admin_business_outlook_content_edit_title')}</h3>

                            <h4 className="text-xl font-semibold text-gray-800 mt-6 mb-4">{t('admin_business_outlook_scope_title')}</h4>
                            {(currentBusinessOutlookContent.businessScopeItems || []).map((item, itemIndex) => (
                                <div key={itemIndex} className="border p-4 rounded-md mb-4 bg-gray-50">
                                    <h5 className="font-medium mb-2">{t('admin_business_outlook_scope_item')} {itemIndex + 1}</h5>
                                    <input
                                        type="text"
                                        placeholder={t('admin_business_outlook_item_title_placeholder')}
                                        value={item.title || ''}
                                        onChange={(e) => handleBusinessScopeItemChange(itemIndex, 'title', e.target.value)}
                                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-2"
                                    />
                                    {(item.points || []).length > 0 && (
                                        <>
                                            <h6 className="font-medium mt-3 mb-1">{t('admin_business_outlook_points_label')}:</h6>
                                            {(item.points || []).map((point, pointIndex) => (
                                                <div key={pointIndex} className="flex items-center mb-2">
                                                    <input
                                                        type="text"
                                                        placeholder={t('admin_business_outlook_point_description_placeholder')}
                                                        value={point || ''}
                                                        onChange={(e) => handleBusinessScopePointChange(itemIndex, pointIndex, e.target.value)}
                                                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mr-2"
                                                    />
                                                    <button
                                                        onClick={() => {
                                                            const updatedItems = [...(currentBusinessOutlookContent.businessScopeItems || [])];
                                                            if (updatedItems[itemIndex] && updatedItems[itemIndex].points) {
                                                                updatedItems[itemIndex].points.splice(pointIndex, 1);
                                                            }
                                                            setCurrentBusinessOutlookContent(prev => ({ ...prev, businessScopeItems: updatedItems }));
                                                        }}
                                                        className="bg-red-400 hover:bg-red-500 text-white py-1 px-2 rounded-md text-sm transition duration-300"
                                                    >
                                                        {t('admin_remove_button')}
                                                    </button>
                                                </div>
                                            ))}
                                            <button
                                                onClick={() => {
                                                    const updatedItems = [...(currentBusinessOutlookContent.businessScopeItems || [])];
                                                    if (updatedItems[itemIndex]) {
                                                        updatedItems[itemIndex].points = [...(updatedItems[itemIndex].points || []), ''];
                                                    }
                                                    setCurrentBusinessOutlookContent(prev => ({ ...prev, businessScopeItems: updatedItems }));
                                                }}
                                                className="bg-blue-400 hover:bg-blue-500 text-white py-1 px-3 rounded-md text-sm mt-2 transition duration-300"
                                            >
                                                {t('admin_add_point_button')}
                                            </button>
                                        </>
                                    )}
                                    {item.text && (
                                        <div>
                                            <label htmlFor={`businessScopeText${itemIndex}`} className="block text-gray-700 text-sm font-bold mb-2">{t('admin_business_outlook_text_content_label')}:</label>
                                            <textarea
                                                id={`businessScopeText${itemIndex}`}
                                                value={item.text || ''}
                                                onChange={(e) => handleBusinessScopeItemChange(itemIndex, 'text', e.target.value)}
                                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-20"
                                            />
                                        </div>
                                    )}
                                    {item.imageUrl && (
                                        <div>
                                            <label htmlFor={`businessScopeImageUrl${itemIndex}`} className="block text-gray-700 text-sm font-bold mb-2">{t('admin_business_outlook_image_url_label')}:</label>
                                            <input
                                                type="text"
                                                id={`businessScopeImageUrl${itemIndex}`}
                                                value={item.imageUrl || ''}
                                                onChange={(e) => handleBusinessScopeItemChange(itemIndex, 'imageUrl', e.target.value)}
                                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                            />
                                            <img src={item.imageUrl} alt={t('admin_business_outlook_preview_alt')} className="mt-2 w-32 h-auto rounded-md" onError={(e) => e.target.src = 'https://placehold.co/100x60/cccccc/333333?text=Image+Error'}/>
                                        </div>
                                    )}
                                </div>
                            ))}

                            <h4 className="text-xl font-semibold text-gray-800 mt-6 mb-4">{t('admin_business_outlook_expansion_title')}</h4>
                            {(currentBusinessOutlookContent.businessExpansionItems || []).map((item, itemIndex) => (
                                <div key={itemIndex} className="border p-4 rounded-md mb-4 bg-gray-50">
                                    <h5 className="font-medium mb-2">{t('admin_business_outlook_expansion_item')} {itemIndex + 1}</h5>
                                    <input
                                        type="text"
                                        placeholder={t('admin_business_outlook_item_title_placeholder')}
                                        value={item.title || ''}
                                        onChange={(e) => handleBusinessExpansionItemChange(itemIndex, 'title', e.target.value)}
                                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-2"
                                    />
                                    {(item.points || []).length > 0 && (
                                        <>
                                            <h6 className="font-medium mt-3 mb-1">{t('admin_business_outlook_points_label')}:</h6>
                                            {(item.points || []).map((point, pointIndex) => (
                                                <div key={pointIndex} className="flex items-center mb-2">
                                                    <input
                                                        type="text"
                                                        placeholder={t('admin_business_outlook_point_description_placeholder')}
                                                        value={point || ''}
                                                        onChange={(e) => handleBusinessExpansionPointChange(itemIndex, pointIndex, e.target.value)}
                                                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mr-2"
                                                    />
                                                    <button
                                                        onClick={() => {
                                                            const updatedItems = [...(currentBusinessOutlookContent.businessExpansionItems || [])];
                                                            if (updatedItems[itemIndex] && updatedItems[itemIndex].points) {
                                                                updatedItems[itemIndex].points.splice(pointIndex, 1);
                                                            }
                                                            setCurrentBusinessOutlookContent(prev => ({ ...prev, businessExpansionItems: updatedItems }));
                                                        }}
                                                        className="bg-red-400 hover:bg-red-500 text-white py-1 px-2 rounded-md text-sm transition duration-300"
                                                    >
                                                        {t('admin_remove_button')}
                                                    </button>
                                                </div>
                                            ))}
                                            <button
                                                onClick={() => {
                                                    const updatedItems = [...(currentBusinessOutlookContent.businessExpansionItems || [])];
                                                    if (updatedItems[itemIndex]) {
                                                        updatedItems[itemIndex].points = [...(updatedItems[itemIndex].points || []), ''];
                                                    }
                                                    setCurrentBusinessOutlookContent(prev => ({ ...prev, businessExpansionItems: updatedItems }));
                                                }}
                                                className="bg-blue-400 hover:bg-blue-500 text-white py-1 px-3 rounded-md text-sm mt-2 transition duration-300"
                                            >
                                                {t('admin_add_point_button')}
                                            </button>
                                        </>
                                    )}
                                </div>
                            ))}
                            <h4 className="text-xl font-semibold text-gray-800 mt-6 mb-4">{t('admin_business_outlook_financial_revenue_title')}</h4>
                            <div className="overflow-x-auto mb-4">
                                <table className="min-w-full bg-white rounded-lg shadow-md">
                                    <thead>
                                        <tr className="bg-blue-100 text-blue-800">
                                            <th className="py-2 px-3 text-left">{t('admin_business_outlook_year_column')}</th>
                                            <th className="py-2 px-3 text-left">{t('admin_business_outlook_revenue_column')}</th>
                                            <th className="py-2 px-3 text-left">{t('admin_business_outlook_sales_column')}</th>
                                            <th className="py-2 px-3 text-left">{t('admin_business_outlook_gross_profit_column')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {((currentBusinessOutlookContent.financialForecast && currentBusinessOutlookContent.financialForecast.revenueTable) || []).map((row, index) => (
                                            <tr key={index} className="border-b border-gray-200">
                                                <td className="py-2 px-3">{row.year || ''}</td>
                                                <td className="py-2 px-3">
                                                    <input type="number" value={row.revenue || ''} onChange={(e) => handleFinancialRevenueChange(index, 'revenue', e.target.value)} className="w-24 border rounded px-2 py-1 text-gray-700" />
                                                </td>
                                                <td className="py-2 px-3">
                                                    <input type="number" value={row.sales || ''} onChange={(e) => handleFinancialRevenueChange(index, 'sales', e.target.value)} className="w-24 border rounded px-2 py-1 text-gray-700" />
                                                </td>
                                                <td className="py-2 px-3">
                                                    <input type="number" value={row.grossProfit || ''} onChange={(e) => handleFinancialRevenueChange(index, 'grossProfit', e.target.value)} className="w-24 border rounded px-2 py-1 text-gray-700" />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <h4 className="text-xl font-semibold text-gray-800 mt-6 mb-4">{t('admin_business_outlook_financial_cost_title')}</h4>
                            {((currentBusinessOutlookContent.financialForecast && currentBusinessOutlookContent.financialForecast.costAssumptions) || []).map((assumption, assumpIndex) => (
                                <div key={assumpIndex} className="border p-4 rounded-md mb-4 bg-gray-50">
                                    <h5 className="font-medium mb-2">{t('admin_business_outlook_assumption_item')} {assumpIndex + 1}</h5>
                                    <input
                                        type="text"
                                        placeholder={t('admin_business_outlook_assumption_type_placeholder')}
                                        value={assumption.type || ''}
                                        onChange={(e) => handleFinancialCostAssumptionChange(assumpIndex, 'type', e.target.value)}
                                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-2"
                                    />
                                    <h6 className="font-medium mt-3 mb-1">{t('admin_business_outlook_details_label')}:</h6>
                                    {(assumption.details || []).map((detail, detailIndex) => (
                                        <div key={detailIndex} className="flex items-center mb-2">
                                            <input
                                                type="text"
                                                placeholder={t('admin_business_outlook_detail_description_placeholder')}
                                                value={detail || ''}
                                                onChange={(e) => handleFinancialCostDetailChange(assumpIndex, detailIndex, e.target.value)}
                                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mr-2"
                                            />
                                            <button
                                                onClick={() => handleRemoveFinancialCostDetail(assumpIndex, detailIndex)}
                                                className="bg-red-400 hover:bg-red-500 text-white py-1 px-2 rounded-md text-sm transition duration-300"
                                            >
                                                {t('admin_remove_button')}
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        onClick={() => handleAddFinancialCostDetail(assumpIndex)}
                                        className="bg-blue-400 hover:bg-blue-500 text-white py-1 px-3 rounded-md text-sm mt-2 transition duration-300"
                                    >
                                        {t('admin_add_detail_button')}
                                    </button>
                                </div>
                            ))}
                            <div>
                                <label htmlFor="investmentOpportunity" className="block text-gray-700 text-sm font-bold mb-2">{t('admin_business_outlook_investment_opportunity_label')}:</label>
                                <textarea
                                    id="investmentOpportunity"
                                    value={((currentBusinessOutlookContent.financialForecast && currentBusinessOutlookContent.financialForecast.investmentOpportunity) || '')}
                                    onChange={handleFinancialInvestmentOpportunityChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-32"
                                />
                            </div>
                            <button
                                onClick={handleUpdateBusinessOutlookContent}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out transform hover:scale-105 mt-6"
                            >
                                {t('admin_save_business_outlook_button')}
                            </button>
                        </div>
                    )}

                    {/* Industry Dynamics Tab - 更新为新闻列表管理 */}
                    {currentAdminTab === 'industryDynamics' && (
                        <div className="space-y-6">
                            <h3 className="text-2xl font-semibold text-gray-800 mb-4">{t('admin_industry_dynamics_edit_title')}</h3>
                            
                            {(currentIndustryDynamicsContent.newsItems || []).map((news, index) => (
                                <div key={news.id || index} className="border p-4 rounded-md mb-4 bg-gray-50">
                                    <h4 className="text-xl font-semibold text-gray-800 mb-2">{t('news_item')} {index + 1}</h4>
                                    <div className="space-y-3">
                                        <div>
                                            <label htmlFor={`newsTitle-${index}`} className="block text-gray-700 text-sm font-bold mb-1">{t('news_title_label')}:</label>
                                            <input
                                                type="text"
                                                id={`newsTitle-${index}`}
                                                placeholder={t('news_title_placeholder')}
                                                value={news.title || ''}
                                                onChange={(e) => handleNewsItemChange(index, 'title', e.target.value)}
                                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor={`newsDate-${index}`} className="block text-gray-700 text-sm font-bold mb-1">{t('news_date_label')}:</label>
                                            <input
                                                type="text"
                                                id={`newsDate-${index}`}
                                                placeholder={t('news_date_placeholder')}
                                                value={news.date || ''}
                                                onChange={(e) => handleNewsItemChange(index, 'date', e.target.value)}
                                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor={`newsMedia-${index}`} className="block text-gray-700 text-sm font-bold mb-1">{t('news_media_label')}:</label>
                                            <input
                                                type="text"
                                                id={`newsMedia-${index}`}
                                                placeholder={t('news_media_placeholder')}
                                                value={news.media || ''}
                                                onChange={(e) => handleNewsItemChange(index, 'media', e.target.value)}
                                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor={`newsContent-${index}`} className="block text-gray-700 text-sm font-bold mb-1">{t('news_content_label')}:</label>
                                            <textarea
                                                id={`newsContent-${index}`}
                                                placeholder={t('news_content_placeholder')}
                                                value={news.content || ''}
                                                onChange={(e) => handleNewsItemChange(index, 'content', e.target.value)}
                                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-24 resize-y"
                                            />
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleRemoveNewsItem(index)}
                                        className="mt-4 bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-md text-sm transition duration-300"
                                    >
                                        {t('remove_news_item_button')}
                                    </button>
                                </div>
                            ))}
                            <button
                                onClick={handleAddNewsItem}
                                className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out transform hover:scale-105"
                            >
                                {t('add_news_item_button')}
                            </button>
                            <button
                                onClick={handleUpdateIndustryDynamicsContent}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out transform hover:scale-105 mt-6"
                            >
                                {t('admin_save_industry_dynamics_button')}
                            </button>
                        </div>
                    )}

                    {/* Contact Info Tab */}
                    {currentAdminTab === 'contact' && (
                        <div className="space-y-6">
                            <h3 className="text-2xl font-semibold text-gray-800 mb-4">{t('admin_contact_info_edit_title')}</h3>
                            <div>
                                <label htmlFor="phone" className="block text-gray-700 text-sm font-bold mb-2">{t('contact_phone_label')}:</label>
                                <input
                                    type="text"
                                    id="phone"
                                    name="phone"
                                    value={currentContactInfo.phone || ''}
                                    onChange={handleContactInfoChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                />
                            </div>
                            <div>
                                <label htmlFor="email" className="block text-gray-700 text-sm font-bold mb-2">{t('contact_email_label')}:</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={currentContactInfo.email || ''}
                                    onChange={handleContactInfoChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                />
                            </div>
                            <div>
                                <label htmlFor="address" className="block text-gray-700 text-sm font-bold mb-2">{t('contact_address_label')}:</label>
                                <textarea
                                    id="address"
                                    name="address"
                                    value={currentContactInfo.address || ''}
                                    onChange={handleContactInfoChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-24"
                                />
                            </div>
                            <button
                                onClick={handleUpdateContactInfo}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out transform hover:scale-105"
                            >
                                {t('admin_save_contact_info_button')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

export default AdminDashboard;
